import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, CloudflareTokenCheck, SecretMetadata } from '../api';
import { humanize } from '../labels';
import { relativeTime } from './project/projectHealth';
import { Field, Input } from './primitives';
import { ErrorNotice, StatusBadge } from './ui';

/** The secret a component's cache provider reads (domain.CacheSecretName). */
const SECRET_NAME = 'CACHE_PURGE_CREDENTIAL';

interface CloudflareTokenFieldProps {
  projectId: string;
  applicationId: string;
  environmentId: string;
  /** A zone is entered: only then can the stored token be checked against it. */
  checkable: boolean;
  /** The zone to check the token against, saved or not. Without it the saved zone is used. */
  zoneId?: string;
  /** Called with every check, so the zone and the permissions can be shown beside the token. */
  onChecked?: (check: CloudflareTokenCheck | null) => void;
  /** Called when the stored token appears or goes: true when one is stored. */
  onStored?: (stored: boolean) => void;
  /** Help beside the token's status. */
  help?: ReactNode;
}

/**
 * The Cloudflare token of one component (CO-201): save, replace and revoke it
 * through the secret store, and check what Cloudflare says about it.
 *
 * The token is write-only. It is read from the field when "Save token" is
 * pressed and the field is emptied at once; it is never put in React state,
 * browser storage or a request other than the one that stores it, and no
 * response returns it. The chip shows what a check found: a token that
 * Cloudflare accepts and that reads the zone is "valid, scope unverified",
 * because purge permission cannot be tested without purging.
 */
export function CloudflareTokenField({ projectId, applicationId, environmentId, checkable, zoneId, onChecked, onStored, help }: CloudflareTokenFieldProps) {
  const input = useRef<HTMLInputElement>(null);
  const [secret, setSecret] = useState<SecretMetadata | null | undefined>(undefined);
  const [check, setCheck] = useState<CloudflareTokenCheck | null>(null);
  const [busy, setBusy] = useState<'save' | 'check' | 'revoke' | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [empty, setEmpty] = useState(false);

  const runCheck = useCallback(async () => {
    setBusy('check');
    setError(null);
    try {
      const result = zoneId ? await api.verifyComponentCloudflare(projectId, applicationId, environmentId, zoneId) : await api.verifyComponentCloudflare(projectId, applicationId, environmentId);
      setCheck(result);
      onChecked?.(result);
    } catch (err) {
      setCheck(null);
      onChecked?.(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }, [projectId, applicationId, environmentId, zoneId, onChecked]);

  useEffect(() => {
    let live = true;
    api
      .listEnvironmentSecrets(environmentId)
      .then(({ secrets }) => {
        if (!live) return;
        const found = secrets.find((s) => s.name === SECRET_NAME && s.application_id === applicationId) ?? null;
        setSecret(found);
        onStored?.(found !== null);
      })
      .catch((err) => {
        if (!live) return;
        setSecret(undefined);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      live = false;
    };
  }, [environmentId, applicationId]);

  async function save() {
    const value = input.current?.value.trim() ?? '';
    if (input.current) input.current.value = '';
    if (!value) {
      setEmpty(true);
      return;
    }
    setEmpty(false);
    setBusy('save');
    setError(null);
    setCheck(null);
    try {
      const written = await api.writeComponentSecret(environmentId, applicationId, SECRET_NAME, value);
      setSecret(written.secret);
      onStored?.(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setBusy(null);
      return;
    }
    setBusy(null);
    if (checkable) await runCheck();
  }

  async function revoke() {
    if (!secret) return;
    setBusy('revoke');
    setError(null);
    try {
      await api.deleteSecret(secret.id);
      setSecret(null);
      setCheck(null);
      onChecked?.(null);
      onStored?.(false);
      setRevoking(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }

  const state = secret === null ? 'not_configured' : check ? check.state : 'unknown';
  const stateTitle = check?.message ?? (check?.state === 'valid' ? 'Purge permission is not tested' : undefined);

  return (
    <div className="cloudflare-token build-form__wide" data-testid="cloudflare-token">
      <Field label="API token" hint={secret ? 'Saved; never shown again' : undefined} error={empty ? 'Paste the token first.' : null}>
        <Input
          ref={input}
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder={secret ? 'Replace the saved token' : 'Zone Read and Cache Purge'}
          disabled={busy !== null}
          data-lpignore="true"
          data-1p-ignore="true"
          data-testid="cloudflare-token-input"
          onChange={() => setEmpty(false)}
          onKeyDown={(e) => {
            // Enter saves the token, not the whole dialog behind it.
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
          }}
        />
      </Field>
      <div className="cloudflare-token__row">
        {secret !== undefined ? (
          <div className="cell-inline" data-testid="cloudflare-token-status">
            <StatusBadge kind="cloudflareToken" value={state} title={stateTitle} testId="cloudflare-token-state" />
            {check ? <span className="muted">Checked {relativeTime(check.checked_at)}</span> : secret ? <span className="muted">Saved {relativeTime(secret.updated_at)}</span> : null}
            {check?.zone ? <code translate="no">{check.zone.name}</code> : null}
            {check?.zone ? <span className="muted">{check.zone.paused ? 'Paused' : humanize(check.zone.status)}</span> : null}
            {check?.expires_at ? <span className="muted">Expires {new Date(check.expires_at).toLocaleDateString()}</span> : null}
            {check?.message && check.state !== 'valid' ? <span className="muted">{check.message}</span> : null}
            {help}
          </div>
        ) : null}
        <div className="row-actions cloudflare-token__actions">
          <button type="button" className="button button--secondary button--small" onClick={() => void save()} disabled={busy !== null} data-testid="cloudflare-token-save">
            {busy === 'save' ? 'Saving' : secret ? 'Replace token' : 'Save token'}
          </button>
          {secret ? (
            <button
              type="button"
              className="button button--secondary button--small"
              onClick={() => void runCheck()}
              disabled={busy !== null || !checkable}
              title={checkable ? undefined : 'Enter the zone first'}
              data-testid="cloudflare-token-check"
            >
              {busy === 'check' ? 'Checking' : 'Check token'}
            </button>
          ) : null}
          {secret && !revoking ? (
            <button type="button" className="button button--danger-quiet button--small" onClick={() => setRevoking(true)} disabled={busy !== null} data-testid="cloudflare-token-revoke">
              Revoke token
            </button>
          ) : null}
          {secret && revoking ? (
            <>
              <button type="button" className="button button--danger-quiet button--small" onClick={() => void revoke()} disabled={busy !== null} data-testid="cloudflare-token-revoke-confirm">
                {busy === 'revoke' ? 'Revoking' : 'Delete the saved token'}
              </button>
              <button type="button" className="button button--secondary button--small" onClick={() => setRevoking(false)} disabled={busy !== null} autoFocus>
                Keep it
              </button>
            </>
          ) : null}
        </div>
      </div>
      <ErrorNotice error={error} />
    </div>
  );
}
